// src/components/SEO/SEOHead.jsx
import React from 'react';

/**
 * Composant réutilisable pour gérer les meta tags SEO par page.
 *
 * ⚙️ Implémentation : React 19 natif.
 * React 19 reconnaît les balises <title>, <meta>, <link>, <script type="application/ld+json">
 * rendues depuis n'importe quel composant et les hisse automatiquement dans <head>.
 * Aucune dépendance externe (pas de react-helmet / react-helmet-async).
 * Doc : https://react.dev/reference/react-dom/components/title
 *
 * Usage :
 *   <SEOHead
 *     title="Bières et Alcools-Mix livrés"
 *     description="Notre sélection de bières du Cameroun..."
 *     canonical="https://bccv.online/products/bieres"
 *   />
 *
 * - `title` : titre de la page (le suffixe "| Boissons du Cameroun" est ajouté automatiquement)
 * - `description` : meta description (~150-160 caractères idéalement)
 * - `canonical` : URL canonique (sans slash final sauf pour /)
 * - `noindex` : true pour bloquer l'indexation (pages privées)
 * - `image` : URL absolue de l'image Open Graph (défaut : logo512.png)
 * - `jsonLd` : objet JSON-LD à inclure en plus (ex. Product pour une page produit)
 */
const SEOHead = ({
  title,
  description,
  canonical,
  noindex = false,
  image = 'https://bccv.online/og-image.png',
  jsonLd = null,
}) => {
  const fullTitle = title
    ? `${title} | Boissons du Cameroun`
    : 'Boissons du Cameroun - Livraison de boissons à domicile à Douala';

  const robotsContent = noindex ? 'noindex, nofollow' : 'index, follow';

  return (
    <>
      <title>{fullTitle}</title>

      {description && <meta name="description" content={description} />}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />

      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={image} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Boissons du Cameroun" />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD additionnel (optionnel) */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </>
  );
};

export default SEOHead;
